import { useEffect, useState } from 'react';
import { Input, InputNumber } from 'antd';
import { BordSwitch } from 'bord-design-system';
import { CountryDefaultCurrencyRole, ICountryDetail, IUpdateCountry } from 'types/countries';
import { BlackCheck } from '../';
import CountryCurrencyDropdown from '../countryCurrencyDropdown/CountryCurrencyDropdown';
import CountryProviderCell from '../countryProviderCell/CountryProviderCell';
import LoaderRow from '../loaderRow/LoaderRow';
import {
  buildCountryRowFieldHasValue,
  buildCountryRowFormState,
  countryPhoneCodeInputNumberProps,
  countryTaxRateInputNumberProps,
  getCountryRowFilledClassName,
  isCountryRowFormInvalid,
  normalizeDocumentExample,
  parseCatalogExchangeRate
} from '../../countriesTab.utils';

import './countryRowForm.scss';

interface ICountryRowForm {
  countryData: ICountryDetail;
  onSave: (data: IUpdateCountry) => void;
}

/**
 * Fila de formulario para completar y guardar un país nuevo antes de persistirlo.
 */
const CountryRowForm = ({ countryData, onSave }: ICountryRowForm) => {
  const {
    code: countryCode,
    flag: countryFlagUrl,
    name: countryName,
    currencyCode: primaryCurrencyCode,
    defaultCurrencyRole: initialDefaultCurrencyRole,
    exchangeRate: catalogExchangeRateValue,
    documentType,
    providers: countryProviders = []
  } = countryData;

  const [formData, setFormData] = useState<IUpdateCountry>();

  const primaryCurrencyLabel = primaryCurrencyCode?.toUpperCase() ?? '';
  const isUsdPrimaryCurrency = primaryCurrencyLabel === 'USD';
  const hasSecondaryCurrency = !isUsdPrimaryCurrency;
  const currentDefaultCurrencyRole =
    formData?.defaultCurrencyRole ?? initialDefaultCurrencyRole ?? CountryDefaultCurrencyRole.PRIMARY;
  const isSecondaryDefault = currentDefaultCurrencyRole === CountryDefaultCurrencyRole.SECONDARY;
  const isCurrencySwitchDisabled = !hasSecondaryCurrency;
  const isCurrencySwitchChecked = hasSecondaryCurrency ? isSecondaryDefault : true;
  const catalogExchangeRate = parseCatalogExchangeRate(catalogExchangeRateValue);

  const handleDefaultCurrencySwitch = (isSecondarySelected: boolean) => {
    if (isCurrencySwitchDisabled || !formData) return;

    setFormData({
      ...formData,
      defaultCurrencyRole: isSecondarySelected
        ? CountryDefaultCurrencyRole.SECONDARY
        : CountryDefaultCurrencyRole.PRIMARY
    });
  };

  const handleDocumentChange = (example: string) => {
    if (!formData) return;

    setFormData({ ...formData, documentExample: normalizeDocumentExample(example) });
  };

  const handleSave = () => {
    if (!formData || isCountryRowFormInvalid(formData, catalogExchangeRate)) return;

    onSave({
      exchangeRate: catalogExchangeRate,
      taxRate: formData.taxRate,
      phoneCode: formData.phoneCode,
      documentExample: formData.documentExample,
      defaultCurrencyRole: formData.defaultCurrencyRole,
      registry: formData.registry,
      buy: formData.buy,
      logisticService: formData.logisticService,
      storage: formData.storage
    });
  };

  const fieldHasValue = buildCountryRowFieldHasValue(formData, catalogExchangeRate, countryProviders.length);
  const isSaveDisabled = isCountryRowFormInvalid(formData, catalogExchangeRate);

  useEffect(() => {
    setFormData(buildCountryRowFormState(countryData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryData]);

  if (!formData) {
    return <LoaderRow />;
  }

  return (
    <tr className="countryTableRow countryRowForm">
      <td className="countryFlagColumn">
        <div className="flagWrapper">
          <div className="flagBox" style={{ backgroundImage: `url(${countryFlagUrl})` }} />
          <div className="countryNameHover">{countryName || 'Sin información del nombre'}</div>
        </div>
      </td>

      <td className="countryCurrencyColumn">
        <CountryCurrencyDropdown selectedCurrencyCode={primaryCurrencyCode} isDisabled />
      </td>

      <td className="exchangeRateColumn">
        <span className="labelCountryInput filledLabel">Tasa de cambio</span>
        <InputNumber
          placeholder={primaryCurrencyCode}
          value={catalogExchangeRate}
          disabled
          className="countryNumberInput readOnlyExchangeRate"
        />
      </td>

      <td className="taxesColumn">
        <span className={getCountryRowFilledClassName('labelCountryInput', fieldHasValue.taxes)}>Impuestos</span>
        <InputNumber
          defaultValue={formData.taxRate}
          onChange={taxRateValue => {
            setFormData({ ...formData, taxRate: taxRateValue });
          }}
          {...countryTaxRateInputNumberProps}
          className={getCountryRowFilledClassName('countryNumberInput', fieldHasValue.taxes, 'filledInput')}
        />
      </td>

      <td className="phoneCodeColumn">
        <span className={getCountryRowFilledClassName('labelCountryInput', fieldHasValue.phoneCode)}>Indicativo</span>
        <InputNumber
          defaultValue={formData.phoneCode}
          onChange={phoneCodeValue => {
            setFormData({ ...formData, phoneCode: phoneCodeValue });
          }}
          {...countryPhoneCodeInputNumberProps}
          className={getCountryRowFilledClassName('countryNumberInput', fieldHasValue.phoneCode, 'filledInput')}
        />
      </td>

      <td className="documentColumn">
        <span className={getCountryRowFilledClassName('labelCountryInput uppercase', fieldHasValue.document)}>
          {documentType}
        </span>
        <Input
          defaultValue={formData.documentExample}
          onChange={event => handleDocumentChange(event.target.value)}
          placeholder={documentType || ''}
          className={getCountryRowFilledClassName('countrySimpleInput', fieldHasValue.document, 'filledInput')}
          value={formData.documentExample}
        />
      </td>

      <td className="providerColumn">
        <CountryProviderCell
          countryCode={countryCode}
          countryProviders={countryProviders}
          hasProviderValue={fieldHasValue.provider}
        />
      </td>

      <td className="registryColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setFormData({ ...formData, registry: !formData.registry });
            }}
            checked={!!formData.registry}
          />
        </div>
      </td>

      <td className="buyColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setFormData({ ...formData, buy: !formData.buy });
            }}
            checked={!!formData.buy}
          />
        </div>
      </td>

      <td className="servicesColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setFormData({ ...formData, logisticService: !formData.logisticService });
            }}
            checked={!!formData.logisticService}
          />
        </div>
      </td>

      <td className="storageColumn">
        <div className="countryRowInputZone">
          <BlackCheck
            handleChangeCheck={() => {
              setFormData({ ...formData, storage: !formData.storage });
            }}
            checked={!!formData.storage}
          />
        </div>
      </td>

      <td className="costsCurrencyColumn">
        <div className="countryRowInputZone">
          <BordSwitch
            variant="segmented"
            checked={isCurrencySwitchChecked}
            onLabel="USD"
            offLabel={isUsdPrimaryCurrency ? '' : primaryCurrencyLabel}
            disabled={isCurrencySwitchDisabled}
            onChange={handleDefaultCurrencySwitch}
          />
        </div>
      </td>

      <td className="actionsColumn">
        <div className="countryRowInputZone">
          <button className="saveButton" type="button" onClick={handleSave} disabled={isSaveDisabled}>
            Guardar
          </button>
        </div>
      </td>
    </tr>
  );
};

export default CountryRowForm;
